/**
 * PersonalDataSection - секция «Персональные данные».
 *
 * Форма с ФИО, телефонами, ИИН и (для кабинета аптеки) временем работы.
 * Состояние формы локальное, начальные значения - из профиля сервера (/api/v2/profile/me).
 * Проверки повторяют серверные (backend/services/profileView.js, parsePersonalData),
 * подсветка незаполненных полей - по заполненности, которую считает сервер.
 */
import React from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  Tooltip
} from '@mui/material';
import { BadgeOutlined, Visibility, VisibilityOff, WarningAmber, AccessTime } from '@mui/icons-material';
import { ZERDE_COLORS } from '../constants';
import SectionHeaderDivider from './SectionHeaderDivider';
import PhoneField from '../PhoneField';
import { cleanPhoneForBackend, phoneNationalDigits, formatPhoneWithSpaces, PHONE_NATIONAL_DIGITS } from '../utils';
import { Completeness, ProfileData, savePersonalData } from '../profileApi';
import { notify } from '../../notifications';
import { apiErrorMessage } from '../../admin/reports/edit/editApi';

export interface PersonalDataSectionProps {
  profile: ProfileData;
  /** Время работы обязательно (кабинет аптеки) */
  requireWorkingHours: boolean;
  completeness: Completeness;
  /** Перечитать профиль после сохранения (ошибки чтения показывает сам) */
  onSaved: () => Promise<void>;
}

const ROUND_THE_CLOCK = 'Круглосуточно';
const HOURS_PRESETS = ['08:00 - 22:00', '08:00 - 23:00', '08:00 - 20:00', '09:00 - 21:00'];
const MIN_WORK_PHONE_DIGITS = 5;
const IIN_DIGITS = 12;
const MAX_NAME_PART = 60;
const MAX_FULL_NAME = 128;
/** Цифры и служебные символы в ФИО; окончательную проверку букв делает сервер */
const NAME_FORBIDDEN = /[0-9_!@#$%^&*()+=[\]{};:"\\|,<>/?~№`]/;

// Колонки полей зависят от ширины секции: в узкой колонке профиля поля идут столбиком
const FIELDS_WIDE = '@container (min-width: 520px)';
const FIELDS_WIDEST = '@container (min-width: 660px)';
const FIELDS_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  alignItems: 'start',
  gap: 2,
  [FIELDS_WIDE]: { gridTemplateColumns: '1fr 1fr' },
  [FIELDS_WIDEST]: { gridTemplateColumns: '1fr 1fr 1fr' }
};
const GROUP_SX = { display: 'block', mb: 1.25, color: '#475569', fontWeight: 700, fontSize: '0.8rem' };

interface FormState {
  lastName: string;
  firstName: string;
  middleName: string;
  userphone: string;
  workPhone: string;
  iin: string;
  workingHours: string;
}

type FieldKey = keyof FormState;

const formOf = (profile: ProfileData): FormState => ({
  lastName: profile.lastName || '',
  firstName: profile.firstName || '',
  middleName: profile.middleName || '',
  userphone: formatPhoneWithSpaces(profile.userphone),
  workPhone: formatPhoneWithSpaces(profile.workPhone),
  iin: (profile.iin || '').replace(/\D/g, ''),
  workingHours: profile.workingHours || ''
});

const squash = (value: string) => value.trim().replace(/\s+/g, ' ');

/** Ошибка части ФИО или null */
const namePartError = (value: string, required: boolean): string | null => {
  const part = squash(value);
  if (!part) return required ? 'Обязательное поле' : null;
  if (part.length > MAX_NAME_PART) return `Не длиннее ${MAX_NAME_PART} символов`;
  if (NAME_FORBIDDEN.test(part) || !/^[^\s'’.-]/.test(part)) return 'Только буквы, пробел, дефис и апостроф';
  return null;
};

/** Разбор «08:00 - 22:00» на часы открытия и закрытия */
const splitHours = (hours: string): { open: string; close: string } => {
  const match = hours.match(/^(\d{1,2}[:.]\d{2})\s*[-–—]\s*(\d{1,2}[:.]\d{2})$/);
  if (!match) return { open: '', close: '' };
  const pad = (time: string) => time.replace('.', ':').padStart(5, '0');
  return { open: pad(match[1]), close: pad(match[2]) };
};

/** Ошибки полей формы - те же правила, что на сервере */
const validate = (form: FormState, requireWorkingHours: boolean): Partial<Record<FieldKey, string>> => {
  const errors: Partial<Record<FieldKey, string>> = {};
  const lastName = namePartError(form.lastName, true);
  const firstName = namePartError(form.firstName, true);
  const middleName = namePartError(form.middleName, false);
  if (lastName) errors.lastName = lastName;
  if (firstName) errors.firstName = firstName;
  if (middleName) errors.middleName = middleName;
  const fullName = [form.lastName, form.firstName, form.middleName].map(squash).filter(Boolean).join(' ');
  if (!errors.lastName && fullName.length > MAX_FULL_NAME) errors.lastName = `ФИО целиком - не длиннее ${MAX_FULL_NAME} символов`;

  const phone = phoneNationalDigits(form.userphone);
  if (phone && phone.length !== PHONE_NATIONAL_DIGITS) errors.userphone = 'Номер: +7 и 10 цифр';
  const workPhone = phoneNationalDigits(form.workPhone);
  if (workPhone && workPhone.length < MIN_WORK_PHONE_DIGITS) errors.workPhone = 'Номер слишком короткий';
  if (form.iin && form.iin.length !== IIN_DIGITS) errors.iin = 'ИИН - ровно 12 цифр';

  if (requireWorkingHours) {
    const hours = form.workingHours;
    if (!hours) errors.workingHours = 'Укажите время работы аптеки';
    else if (hours !== ROUND_THE_CLOCK) {
      const { open, close } = splitHours(hours);
      if (!open || !close) errors.workingHours = 'Укажите время открытия и закрытия';
    }
  }
  return errors;
};

const PersonalDataSection: React.FC<PersonalDataSectionProps> = ({
  profile,
  requireWorkingHours,
  completeness,
  onSaved
}) => {
  const [form, setForm] = React.useState<FormState>(() => formOf(profile));
  const [showIIN, setShowIIN] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  // После сохранения или обновления профиля форма берет значения сервера
  React.useEffect(() => {
    setForm(formOf(profile));
    setSubmitted(false);
  }, [profile]);

  const set = (field: FieldKey) => (value: string) => setForm(prev => ({ ...prev, [field]: value }));

  const initial = React.useMemo(() => formOf(profile), [profile]);
  const dirty = (Object.keys(initial) as FieldKey[]).some(key =>
    key === 'userphone' || key === 'workPhone'
      ? cleanPhoneForBackend(form[key]) !== cleanPhoneForBackend(initial[key])
      : squash(form[key]) !== squash(initial[key])
  );

  const errors = validate(form, requireWorkingHours);
  const hasErrors = Object.keys(errors).length > 0;

  // Незаполненные обязательные поля - по расчету сервера; пока поле не тронуто, подсвечивается оно
  const missingKeys: Record<string, boolean> = {};
  completeness.items.forEach(item => {
    if (!item.done) missingKeys[item.key] = true;
  });
  const personalMissing = completeness.items.filter(item => !item.done && item.key in initial);

  /** Подсказка поля: ошибка формата сразу, пустое обязательное - после попытки сохранить или если сервер считает его незаполненным */
  const fieldState = (field: FieldKey, hint: string): { error: boolean; helperText: string } => {
    const message = errors[field];
    const empty = !squash(form[field]) || form[field] === '+7 ';
    const unchangedMissing = Boolean(missingKeys[field]) && squash(form[field]) === squash(initial[field]);
    if (message && (!empty || submitted)) return { error: true, helperText: message };
    if (unchangedMissing) return { error: true, helperText: 'Не заполнено' };
    return { error: false, helperText: hint };
  };

  const hours = form.workingHours;
  const is24Hours = hours === ROUND_THE_CLOCK;
  const { open: openTime, close: closeTime } = splitHours(hours);

  const setHours = (open: string, close: string) => {
    set('workingHours')(open || close ? `${open || '08:00'} - ${close || '22:00'}` : '');
  };

  const handleToggle24Hours = (checked: boolean) => {
    if (checked) set('workingHours')(ROUND_THE_CLOCK);
    else setHours(openTime || '08:00', closeTime || '22:00');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (hasErrors) {
      notify.warning('Проверьте выделенные поля');
      return;
    }
    setSaving(true);
    try {
      const result = await savePersonalData({
        lastName: squash(form.lastName),
        firstName: squash(form.firstName),
        middleName: squash(form.middleName),
        userphone: cleanPhoneForBackend(form.userphone),
        workPhone: cleanPhoneForBackend(form.workPhone),
        iin: form.iin,
        workingHours: requireWorkingHours ? hours : initial.workingHours
      });
      if (result.message === 'Изменений нет') notify.info(result.message);
      else if (result.biometricsReset) notify.warning(result.message);
      else notify.success(result.message);
      await onSaved();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось сохранить персональные данные'));
    } finally {
      setSaving(false);
    }
  };

  const incomplete = !completeness.personalComplete;
  const hoursState = fieldState('workingHours', '');

  return (
    <Paper
      id="section-personal"
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        borderRadius: '20px',
        bgcolor: '#ffffff',
        border: incomplete ? '1.5px solid #fed7aa' : '1px solid #e2e8f0',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.03)',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        scrollMarginTop: '24px'
      }}
    >
      <SectionHeaderDivider
        id="header-personal"
        icon={<BadgeOutlined sx={{ fontSize: 22 }} />}
        title="Персональные данные"
        subtitle={incomplete
          ? 'Обязательные поля отмечены звездочкой. Пока они не заполнены, другие разделы портала закрыты'
          : 'Обязательные поля отмечены звездочкой'}
        badge={
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
            Логин: {profile.username}
          </Typography>
        }
      />

      {incomplete && personalMissing.length > 0 && (
        <Alert
          severity="warning"
          icon={<WarningAmber fontSize="inherit" />}
          sx={{ borderRadius: '12px', border: '1px solid #fed7aa', bgcolor: '#fff7ed', color: '#9a3412' }}
        >
          Не заполнено: <strong>{personalMissing.map(item => item.title).join(', ')}</strong>. Заполните и сохраните.
        </Alert>
      )}

      {/* Контейнер формы: число колонок полей - по ширине самой секции, а не окна */}
      <Box component="form" noValidate onSubmit={handleSubmit} sx={{ containerType: 'inline-size', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography variant="caption" sx={GROUP_SX}>ФИО</Typography>
          <Box sx={FIELDS_GRID_SX}>
            <TextField
              label="Фамилия *"
              fullWidth
              size="small"
              value={form.lastName}
              onChange={e => set('lastName')(e.target.value)}
              placeholder="Иванов"
              inputProps={{ maxLength: MAX_NAME_PART, autoComplete: 'family-name' }}
              {...fieldState('lastName', '')}
            />
            <TextField
              label="Имя *"
              fullWidth
              size="small"
              value={form.firstName}
              onChange={e => set('firstName')(e.target.value)}
              placeholder="Иван"
              inputProps={{ maxLength: MAX_NAME_PART, autoComplete: 'given-name' }}
              {...fieldState('firstName', '')}
            />
            <TextField
              label="Отчество"
              fullWidth
              size="small"
              value={form.middleName}
              onChange={e => set('middleName')(e.target.value)}
              placeholder="Иванович"
              inputProps={{ maxLength: MAX_NAME_PART, autoComplete: 'additional-name' }}
              {...fieldState('middleName', 'Необязательно')}
            />
          </Box>
        </Box>

        <Box>
          <Typography variant="caption" sx={GROUP_SX}>Контакты и ИИН</Typography>
          <Box sx={FIELDS_GRID_SX}>
            <PhoneField
              label="Номер телефона *"
              value={form.userphone}
              onChange={set('userphone')}
              {...fieldState('userphone', 'Мобильный: +7 и 10 цифр')}
            />
            <PhoneField
              label="Рабочий телефон *"
              value={form.workPhone}
              onChange={set('workPhone')}
              {...fieldState('workPhone', 'Рабочий или телефон филиала')}
            />
            <TextField
              label="ИИН *"
              fullWidth
              size="small"
              type={showIIN ? 'text' : 'password'}
              value={form.iin}
              onChange={e => set('iin')(e.target.value.replace(/\D/g, '').slice(0, IIN_DIGITS))}
              placeholder="12 цифр"
              inputProps={{ maxLength: IIN_DIGITS, inputMode: 'numeric', autoComplete: 'off' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showIIN ? 'Скрыть ИИН' : 'Показать ИИН'} arrow>
                      <IconButton size="small" onClick={() => setShowIIN(prev => !prev)} aria-label={showIIN ? 'Скрыть ИИН' : 'Показать ИИН'}>
                        {showIIN ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
              {...fieldState('iin', `${form.iin.length}/${IIN_DIGITS} цифр`)}
            />
          </Box>
        </Box>

        {requireWorkingHours && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
              <Typography variant="caption" sx={{ ...GROUP_SX, mb: 0 }}>Время работы аптеки *</Typography>
              <FormControlLabel
                control={<Switch size="small" checked={is24Hours} onChange={e => handleToggle24Hours(e.target.checked)} />}
                label={
                  <Typography variant="caption" sx={{ fontWeight: 600, color: is24Hours ? ZERDE_COLORS.primary : '#64748b' }}>
                    Круглосуточно
                  </Typography>
                }
                sx={{ m: 0, gap: 0.5 }}
              />
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: '12px',
                bgcolor: hoursState.error ? '#fff7ed' : '#f8fafc',
                border: `1px solid ${hoursState.error ? '#fed7aa' : '#e2e8f0'}`,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: 2,
                rowGap: 1.25
              }}
            >
              {is24Hours ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <AccessTime sx={{ color: ZERDE_COLORS.emerald, fontSize: 18 }} />
                  <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                    Аптека работает круглосуточно, без перерывов
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '1 1 260px', maxWidth: 340 }}>
                    <TextField
                      label="Открытие"
                      type="time"
                      size="small"
                      fullWidth
                      value={openTime}
                      onChange={e => setHours(e.target.value, closeTime)}
                      error={hoursState.error}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      sx={{ bgcolor: '#ffffff' }}
                    />
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>–</Typography>
                    <TextField
                      label="Закрытие"
                      type="time"
                      size="small"
                      fullWidth
                      value={closeTime}
                      onChange={e => setHours(openTime, e.target.value)}
                      error={hoursState.error}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      sx={{ bgcolor: '#ffffff' }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    {HOURS_PRESETS.map(preset => (
                      <Chip
                        key={preset}
                        label={preset}
                        size="small"
                        clickable
                        onClick={() => set('workingHours')(preset)}
                        variant={hours === preset ? 'filled' : 'outlined'}
                        color={hours === preset ? 'primary' : 'default'}
                        sx={{ height: 24, fontSize: '0.72rem', fontWeight: 500, bgcolor: hours === preset ? undefined : '#ffffff' }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>
            {hoursState.error && hoursState.helperText && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#c2410c', fontWeight: 600 }}>
                {hoursState.helperText}
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ pt: 2, borderTop: '1px solid #f1f5f9', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption" sx={{ color: '#c2410c', fontWeight: 600 }}>
            {dirty ? 'Есть несохраненные изменения' : ''}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {dirty && !saving && (
              <Button
                variant="text"
                onClick={() => {
                  setForm(initial);
                  setSubmitted(false);
                }}
                sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b', borderRadius: '8px' }}
              >
                Отменить
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disableElevation
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{
                bgcolor: ZERDE_COLORS.primary,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': { bgcolor: '#1d4ed8' }
              }}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default PersonalDataSection;
